import React, { useRef, useState, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { ChevronDown, List } from 'lucide-react';
import styles from './CommentEditorToolbar.module.scss';

export interface CommentEditorToolbarActiveFormats {
  bold: boolean;
  italic: boolean;
  strikethrough: boolean;
  code: boolean;
}

export interface CommentEditorToolbarProps {
  onBold: () => void;
  onItalic: () => void;
  onSaveSelection?: () => void;
  onStrikethrough?: () => void;
  onCode?: () => void;
  onClearFormatting?: () => void;
  onBulletList?: () => void;
  onNumberedList?: () => void;
  activeFormats?: CommentEditorToolbarActiveFormats;
}

export default function CommentEditorToolbar({
  onBold,
  onItalic,
  onSaveSelection,
  onStrikethrough,
  onCode,
  onClearFormatting,
  onBulletList,
  onNumberedList,
  activeFormats,
}: CommentEditorToolbarProps) {
  const [moreOpen, setMoreOpen] = useState(false);
  const [listOpen, setListOpen] = useState(false);
  const [moreRect, setMoreRect] = useState<DOMRect | null>(null);
  const [listRect, setListRect] = useState<DOMRect | null>(null);
  const moreBtnRef = useRef<HTMLButtonElement>(null);
  const listBtnRef = useRef<HTMLButtonElement>(null);

  const openDropdown = (
    open: boolean,
    setRect: (r: DOMRect | null) => void,
    btnRef: React.RefObject<HTMLButtonElement | null>,
    setOpen: (v: boolean) => void,
  ) => {
    if (!open) {
      setRect(null);
      return;
    }
    setRect(btnRef.current?.getBoundingClientRect() ?? null);
    const onDocClick = () => setOpen(false);
    const t = setTimeout(
      () => document.addEventListener('click', onDocClick),
      0,
    );
    return () => {
      clearTimeout(t);
      document.removeEventListener('click', onDocClick);
    };
  };

  useEffect(() => {
    return openDropdown(moreOpen, setMoreRect, moreBtnRef, setMoreOpen);
  }, [moreOpen]);

  useEffect(() => {
    return openDropdown(listOpen, setListRect, listBtnRef, setListOpen);
  }, [listOpen]);

  const runAndCloseMore = (fn: (() => void) | undefined) => {
    fn?.();
    setMoreOpen(false);
  };

  const runAndCloseList = (fn: (() => void) | undefined) => {
    fn?.();
    setListOpen(false);
  };

  const isMoreFormatActive = Boolean(
    activeFormats?.strikethrough || activeFormats?.code,
  );

  return (
    <div className={styles.toolbar}>
      <button
        type="button"
        className={`${styles.btn} ${activeFormats?.bold ? styles.btnActive : ''}`}
        aria-label="Bold"
        disabled={isMoreFormatActive}
        onClick={(e) => {
          e.preventDefault();
          onBold();
        }}
      >
        <strong>B</strong>
      </button>
      <button
        type="button"
        className={`${styles.btn} ${activeFormats?.italic ? styles.btnActive : ''}`}
        aria-label="Italic"
        disabled={isMoreFormatActive}
        onClick={(e) => {
          e.preventDefault();
          onItalic();
        }}
      >
        <em>I</em>
      </button>
      <span className={styles.sep} />
      <div className={styles.moreWrap}>
        <button
          ref={moreBtnRef}
          type="button"
          className={`${styles.btn} ${moreOpen || activeFormats?.strikethrough || activeFormats?.code ? styles.btnActive : ''}`}
          aria-label="More options"
          aria-expanded={moreOpen}
          onMouseDown={() => onSaveSelection?.()}
          onClick={(e) => {
            e.stopPropagation();
            setMoreOpen((o) => !o);
          }}
        >
          <span>⋯</span>
        </button>
        {moreOpen &&
          moreRect &&
          createPortal(
            <div
              className={styles.moreDropdown}
              style={{
                top: moreRect.bottom + 4,
                left: moreRect.left,
              }}
              onClick={(e) => e.stopPropagation()}
              role="menu"
            >
              <button
                type="button"
                className={styles.moreItem}
                role="menuitem"
                disabled={Boolean(activeFormats?.code)}
                onClick={() => runAndCloseMore(onStrikethrough)}
              >
                Strikethrough
              </button>
              <button
                type="button"
                className={styles.moreItem}
                role="menuitem"
                disabled={Boolean(activeFormats?.strikethrough)}
                onClick={() => runAndCloseMore(onCode)}
              >
                Code
              </button>
              <div className={styles.moreSep} />
              <button
                type="button"
                className={styles.moreItem}
                role="menuitem"
                onClick={() => runAndCloseMore(onClearFormatting)}
              >
                Clear formatting
              </button>
            </div>,
            document.body,
          )}
      </div>
      <span className={styles.sep} />
      <div className={styles.listWrap}>
        <button
          ref={listBtnRef}
          type="button"
          className={styles.btn}
          aria-label="List"
          aria-expanded={listOpen}
          disabled={isMoreFormatActive}
          onClick={(e) => {
            e.stopPropagation();
            setListOpen((o) => !o);
          }}
        >
          <List size={16} />
          <ChevronDown size={14} />
        </button>
        {listOpen &&
          listRect &&
          createPortal(
            <div
              className={styles.moreDropdown}
              style={{
                top: listRect.bottom + 4,
                left: listRect.left,
              }}
              onClick={(e) => e.stopPropagation()}
              role="menu"
            >
              <button
                type="button"
                className={styles.moreItem}
                role="menuitem"
                onClick={() => runAndCloseList(onBulletList)}
              >
                Bullet list
              </button>
              <button
                type="button"
                className={styles.moreItem}
                role="menuitem"
                onClick={() => runAndCloseList(onNumberedList)}
              >
                Numbered list
              </button>
            </div>,
            document.body,
          )}
      </div>
      <div className={styles.spacer} />
    </div>
  );
}
